import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { isModalOpen, setToastMessage } from '../redux/FlowReducer';
import VariableAutocomplete from '../VariableAutocomplete';
import PanelShell from './PanelShell';
import { Box, Typography } from '@mui/material';

const ArithmeticPanel = ({ nodeType, nodeId, data, onChange }) => {
  const dispatch = useDispatch();

  // useSelector'ları component seviyesinde çağır
  const rawVars = useSelector((state) => state.flow.vars);
  const rawTags = useSelector((state) => state.flow.tags);
  const rawLocalVars = useSelector((state) => state.flow.localVars);

  // Null check ve default değerler için useMemo kullan
  const vars = useMemo(() => rawVars || [], [rawVars]);
  const tags = useMemo(() => rawTags || [], [rawTags]);
  const localVars = useMemo(() => rawLocalVars || [], [rawLocalVars]);

  const [expression, setExpression] = useState('');
  const [expressionId, setExpressionId] = useState('');
  const [outputVariable, setOutputVariable] = useState(null);
  const [focusedElement, setFocusedElement] = useState(null); // 'expression' veya 'output'

  const dateTimeFunctions = [
    { name: 'Now', description: 'Current date and time' },
    { name: 'Now.Year', description: 'Current year' },
    { name: 'Now.Month', description: 'Current month' },
    { name: 'Now.Day', description: 'Current day' },
    { name: 'Now.Hour', description: 'Current hour' },
    { name: 'Now.Minute', description: 'Current minute' },
    { name: 'Now.Second', description: 'Current second' },
    { name: 'AddDays', description: 'Add days to date' },
    { name: 'AddMonths', description: 'Add months to date' },
    { name: 'AddYears', description: 'Add years to date' },
    { name: 'AddHours', description: 'Add hours to date' },
    { name: 'AddMinutes', description: 'Add minutes to date' },
    { name: 'DaysBetween', description: 'Days difference between two dates' },
    { name: 'MonthsBetween', description: 'Months difference between two dates' },
    { name: 'YearsBetween', description: 'Years difference between two dates' }
  ];

  const mathConstants = [
    { name: 'E', value: Math.E },
    { name: 'PI', value: Math.PI }
  ];

  const allOperations = [
    {
      type: 'basic',
      ops: ['+', '-', '*', '/', '^', '(', ')']
    },
    {
      type: 'math',
      ops: [
        'Abs', 'Acos', 'Acosh', 'Asin', 'Asinh', 'Atan', 'Atan2', 'Atanh',
        'Cbrt', 'Ceiling', 'Cos', 'Cosh', 'Exp', 'Floor', 'IEEERemainder',
        'Log', 'Log10', 'Max', 'Min', 'Pow', 'Round', 'Sign', 'Sin', 'Sinh',
        'Sqrt', 'Tan', 'Tanh', 'Truncate'
      ]
    },
    {
      type: 'datetime',
      ops: dateTimeFunctions.map(f => f.name)
    }
  ];

  useEffect(() => {
    if (!data) return;

    console.log('ArithmeticPanel useEffect - data:', data);
    console.log('ArithmeticPanel useEffect - vars:', vars.length, 'tags:', tags.length, 'localVars:', localVars.length);

    setExpression(data.expression_view || data.expression || '');
    setExpressionId(data.expressionId || '');

    if (data.outputVariable) {
      console.log('Processing outputVariable:', data.outputVariable);

      try {
        let prefix = '', id = '';
        const underscoreIndex = data.outputVariable.indexOf('_');

        if (underscoreIndex !== -1) {
          prefix = data.outputVariable.substring(0, underscoreIndex);
          id = data.outputVariable.substring(underscoreIndex + 1);
        }

        console.log('Parsed prefix:', prefix, 'id:', id);

        let foundVariable;
        switch(prefix) {
          case 'lv':
            foundVariable = localVars.find(v => v.name === id);
            console.log('Looking for LV:', id, 'found:', foundVariable);
            if (foundVariable) {
              setOutputVariable({
                variableId: foundVariable.name,
                variableName: foundVariable.name,
                variableType: 'LV'
              });
            }
            break;
          case 'tag':
            foundVariable = tags.find(t => {
              const tagId = t.tagId !== undefined ? t.tagId : t.tagID;
              return String(tagId) === String(id);
            });
            console.log('Looking for TAG:', id, 'found:', foundVariable);
            if (foundVariable) {
              setOutputVariable({
                variableId: foundVariable.tagId !== undefined ? foundVariable.tagId : foundVariable.tagID,
                variableName: foundVariable.tagName,
                variableType: 'T'
              });
            }
            break;
          case 'var':
            foundVariable = vars.find(v => {
              const varId = v.varId !== undefined ? v.varId : v.varID;
              return String(varId) === String(id);
            });
            console.log('Looking for VAR:', id, 'found:', foundVariable);
            if (foundVariable) {
              const varId = foundVariable.varId !== undefined ? foundVariable.varId : foundVariable.varID;
              const varName = foundVariable.varName || foundVariable.name;
              console.log('Setting output variable - varId:', varId, 'varName:', varName);
              setOutputVariable({
                variableId: varId,
                variableName: varName,
                variableType: 'V'
              });
            } else if (vars.length === 0) {
              // vars henüz yüklenmemişse, geçici olarak sadece ID'yi göster
              console.log('Vars not loaded yet, using fallback');
              setOutputVariable({
                variableId: id,
                variableName: `Variable [${id}]`,
                variableType: 'V'
              });
            }
            break;
          default:
            console.warn('Unknown variable type:', prefix);
            break;
        }
      } catch (error) {
        console.error('Error processing output variable:', error);
        setOutputVariable(null);
      }
    }
  }, [data, vars, tags, localVars]);

  // handleExpressionChange fonksiyonu
  const handleExpressionChange = (e) => {
    const newExpression = e.target.value;
    let newExpressionId = '';

    // Operatörleri ve değişkenleri ayırmak için geliştirilmiş regex
    const parts = newExpression.split(/(\s+)/).filter(Boolean);

    parts.forEach((part, index) => {
      const trimmedPart = part.trim();
      if (!trimmedPart) return;

      // Operatör kontrolü - operatörü koruyarak işleme
      if (/^[-+*/()]$/.test(trimmedPart)) {
        newExpressionId += trimmedPart;
        return;
      }

      // Değişken kontrolü
      const variable = [...vars, ...tags, ...localVars].find(v =>
        v.type === 'LV' ? v.name === trimmedPart :
        v.type === 'T' ? v.tagName === trimmedPart :
        v.type === 'V' ? v.varName === trimmedPart :
        false
      );

      if (variable) {
        if (index > 0 && !/\s$/.test(newExpressionId)) {
          newExpressionId += ' ';
        }

        switch(variable.type) {
          case 'V':
            newExpressionId += `var_${variable.varID}`;
            break;
          case 'T':
            newExpressionId += `tag_${variable.tagID}`;
            break;
          case 'LV':
            newExpressionId += `lv_${variable.name}`;
            break;
        }

        if (index < parts.length - 1) {
          newExpressionId += ' ';
        }
      } else {
        if (index > 0 && !/\s$/.test(newExpressionId)) {
          newExpressionId += ' ';
        }
        newExpressionId += trimmedPart;
        if (index < parts.length - 1) {
          newExpressionId += ' ';
        }
      }
    });

    setExpression(newExpression);
    setExpressionId(newExpressionId.trim());
  };

  const handleVariableSelect = useCallback((variable) => {
    if (!variable) return;

    console.log('Selected variable:', variable);

    const varType = variable.type;
    const varName = variable.displayName || variable.name;

    // Get the ID based on type
    let varId;
    if (varType === 'T') {
      varId = variable.tagID || variable.tagId || variable.id;
    } else if (varType === 'V') {
      varId = variable.varID || variable.varId || variable.id;
    } else if (varType === 'LV') {
      varId = variable.name || variable.id;
    }

    if (focusedElement === 'expression') {
      // Add to expression
      const prevTrimmed = expression.trim();
      const lastChar = prevTrimmed.slice(-1);
      const needsSpace = prevTrimmed && !/[-+*/()]/.test(lastChar);
      const newExpression = `${prevTrimmed}${needsSpace ? ' ' : ''}${varName}`;
      setExpression(newExpression);

      // Build variableId for expressionId
      let variableId;
      if (varType === 'V') {
        variableId = `var_${varId}`;
      } else if (varType === 'T') {
        variableId = `tag_${varId}`;
      } else if (varType === 'LV') {
        variableId = `lv_${varId}`;
      }

      const prevIdTrimmed = expressionId.trim();
      const lastIdChar = prevIdTrimmed.slice(-1);
      const needsIdSpace = prevIdTrimmed && !/[-+*/()]/.test(lastIdChar);
      const newExpressionId = `${prevIdTrimmed}${needsIdSpace ? ' ' : ''}${variableId}`;
      setExpressionId(newExpressionId);

    } else if (focusedElement === 'output') {
      // Set as output variable
      setOutputVariable({
        variableId: varId,
        variableName: varName,
        variableType: varType
      });
    }
  }, [focusedElement, expression, expressionId]);


  const parseExpressionForSave = (expr) => {
    if (!expr) return '';

    const parts = expr.split(/\s+/);
    const parsedParts = parts.map(part => {
      // Operatörleri doğrudan kullan
      if (/^[-+*/()]+$/.test(part)) {
        return part;
      }

      // Her bir parçayı değişken isimlerine göre kontrol et
      const foundVar = vars.find(v => v.varName === part);
      if (foundVar) {
        // varId/varID kontrolü
        const varId = foundVar.varId !== undefined ? foundVar.varId : foundVar.varID;
        return `var_${varId}`;
      }

      const foundTag = tags.find(t => t.tagName === part);
      if (foundTag) {
        // tagId/tagID kontrolü
        const tagId = foundTag.tagId !== undefined ? foundTag.tagId : foundTag.tagID;
        return `tag_${tagId}`;
      }

      const foundLV = localVars.find(lv => lv.name === part);
      if (foundLV) {
        return `lv_${foundLV.name}`;
      }

      // Eğer değişken bulunamadıysa, değeri olduğu gibi kullan
      return part;
    });

    return parsedParts.join(' ');
  };



  const handleSave = () => {
    if (!outputVariable || !expression.trim()) {
      dispatch(setToastMessage({
        type: 'error',
        message: 'Please fill in all fields.'
      }));
      return;
    }

    try {
      // İfadeyi yeniden işleyerek doğru ID'leri oluştur
      const parsedExpressionId = parseExpressionForSave(expression);

      console.log("Values to be saved:", {
        expression,
        parsedExpressionId, // Yeni oluşturulan expressionId
        outputVariable
      });

      // Çıkış değişkeni için ID oluştur
      let outputVarString;
      if (outputVariable.variableType === 'V') {
        outputVarString = `var_${outputVariable.variableId}`;
      } else if (outputVariable.variableType === 'T') {
        outputVarString = `tag_${outputVariable.variableId}`;
      } else if (outputVariable.variableType === 'LV') {
        outputVarString = `lv_${outputVariable.variableId}`;
      } else {
        outputVarString = outputVariable.variableId;
      }

      console.log("Saving outputVarString:", outputVarString);

      // ElementDetail için ID hazırla
      const elementDetail = [{
        elementDetailVariableId: outputVariable.variableId || '',
        elementDetailVariableType: outputVariable.variableType
      }];

      const updatedData = {
        ...data,
        expression: expression,
        expression_view: expression,
        expressionId: parsedExpressionId, // Yeni oluşturulan ID'yi kullan
        outputVariable: outputVarString,
        Title: `${outputVariable.variableName} = ${expression}`,
        label: 'Math',
        icon: 'math-icon',
        ElementDetail: elementDetail
      };

      console.log('Final data to save:', updatedData);

      onChange(updatedData);
      dispatch(isModalOpen());
      dispatch(setToastMessage({ type: 'success', message: 'Aritmetik işlem kaydedildi.' }));

    } catch (error) {
      console.error('Save error:', error);
      dispatch(setToastMessage({
        type: 'error',
        message: `An error occurred while saving: ${error.message}`
      }));
    }
  };

  const evaluateExpression = (expr) => {
    if (!expr) return [];

    const parts = expr.split(/(\s+)/).filter(Boolean);
    const validOperators = allOperations[0]?.ops || [];
    const validFunctions = [...(allOperations[1]?.ops || []), ...(allOperations[2]?.ops || [])];
    const validVariables = [
      ...vars.map(v => v.varName),
      ...tags.map(t => t.tagName),
      ...localVars.map(lv => lv.name),
      ...mathConstants.map(c => c.name)
    ];

    return parts.map(part => {
      if (validOperators.includes(part) || !isNaN(part)) {
        return { value: part, isValid: true };
      } else if (validFunctions.includes(part)) {
        return { value: part, isValid: true, isFunction: true };
      } else if (validVariables.includes(part)) {
        return { value: part, isValid: true };
      } else {
        return { value: part, isValid: false };
      }
    });
  };

  const renderColoredExpression = () => {
    const outputName = outputVariable?.variableName || 'Output';
    const fullExpression = `${outputName} = ${expression}`;
    const evaluatedParts = evaluateExpression(fullExpression);

    return evaluatedParts.map((part, index) => {
      let color = part.isValid ? 'black' : 'red';
      if ('()'.includes(part.value)) color = 'blue';
      else if ('+-*/^='.includes(part.value)) color = 'green';
      else if (part.isFunction) color = 'orange';
      else if ([...vars, ...tags, ...localVars].some(v =>
        v.type === 'LV' ? v.name === part.value :
        v.type === 'T' ? v.tagName === part.value :
        v.type === 'V' ? v.varName === part.value : false
      ) || mathConstants.some(c => c.name === part.value)) {
        color = 'purple';
      }
      return <span key={index} style={{ color }}>{part.value}</span>;
    });
  };

  if (nodeType !== "Arithmetic") return null;

  return (
    <PanelShell title="Aritmetik İşlem" onSave={handleSave}>
      <div style={{
        marginBottom: '15px',
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        minHeight: '30px',
        backgroundColor: 'white'
      }}>
        {renderColoredExpression()}
      </div>

      <textarea
        value={expression}
        onChange={handleExpressionChange}
        onFocus={() => setFocusedElement('expression')}
        style={{
          width: '100%',
          padding: '5px',
          marginBottom: '15px',
          minHeight: '100px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          borderColor: focusedElement === 'expression' ? '#6a1b9a' : '#ccc'
        }}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '100%', overflow: 'hidden' }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
            Add Variable to Expression
          </Typography>
          <VariableAutocomplete
            value={null}
            onChange={(event, variable) => {
              if (variable) {
                // Expression'a değişken ekle
                const varType = variable.type;
                const varName = variable.displayName || variable.name;
                let varId;
                if (varType === 'T') {
                  varId = variable.tagID || variable.tagId || variable.id;
                } else if (varType === 'V') {
                  varId = variable.varID || variable.varId || variable.id;
                } else if (varType === 'LV') {
                  varId = variable.name || variable.id;
                }

                const prevTrimmed = expression.trim();
                const lastChar = prevTrimmed.slice(-1);
                const needsSpace = prevTrimmed && !/[-+*/()]/.test(lastChar);
                const newExpression = `${prevTrimmed}${needsSpace ? ' ' : ''}${varName}`;
                setExpression(newExpression);

                let variableId;
                if (varType === 'V') {
                  variableId = `var_${varId}`;
                } else if (varType === 'T') {
                  variableId = `tag_${varId}`;
                } else if (varType === 'LV') {
                  variableId = `lv_${varId}`;
                }

                const prevIdTrimmed = expressionId.trim();
                const lastIdChar = prevIdTrimmed.slice(-1);
                const needsIdSpace = prevIdTrimmed && !/[-+*/()]/.test(lastIdChar);
                const newExpressionId = `${prevIdTrimmed}${needsIdSpace ? ' ' : ''}${variableId}`;
                setExpressionId(newExpressionId);
              }
            }}
            placeholder="Search tags and variables..."
            showConstants={false}
          />
        </Box>

        <Box>
          <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
            Output Variable
          </Typography>
          <VariableAutocomplete
            value={outputVariable ? {
              id: outputVariable.variableId,
              displayName: outputVariable.variableName,
              type: outputVariable.variableType
            } : null}
            onChange={(event, variable) => {
              if (variable) {
                // Output variable'ı ayarla
                const varType = variable.type;
                const varName = variable.displayName || variable.name;
                let varId;
                if (varType === 'T') {
                  varId = variable.tagID || variable.tagId || variable.id;
                } else if (varType === 'V') {
                  varId = variable.varID || variable.varId || variable.id;
                } else if (varType === 'LV') {
                  varId = variable.name || variable.id;
                }
                setOutputVariable({
                  variableId: varId,
                  variableName: varName,
                  variableType: varType
                });
              } else {
                setOutputVariable(null);
              }
            }}
            placeholder="Select output variable..."
            showConstants={false}
          />
          {outputVariable && (
            <Box sx={{ mt: 1, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Selected: {outputVariable.variableName}
              </Typography>
            </Box>
          )}
        </Box>

        <div style={{ marginBottom: '15px' }}>
          <strong>Operations:</strong>
          <div style={{
            maxHeight: '150px',
            overflowY: 'auto',
            border: '1px solid #ccc',
            padding: '5px',
            backgroundColor: 'white',
            borderRadius: '4px'
          }}>
            {allOperations.map((opGroup, index) => (
              <div key={index}>
                {index === 1 && <hr style={{ margin: '10px 0' }} />}
                {opGroup.ops.map(op => (
                  <button
                    key={op}
                    onClick={() => setExpression(prev => prev + ' ' + op + ' ')}
                    style={{
                      marginRight: '5px',
                      marginBottom: '5px',
                      padding: '5px 10px',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      backgroundColor: opGroup.type === 'basic' ? '#e0e0e0' : '#d1c4e9'
                    }}
                  >
                    {op}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <strong>Math Class Constants:</strong>
          {mathConstants.map(constant => (
            <button
              key={constant.name}
              onClick={() => setExpression(prev => prev + ' ' + constant.name + ' ')}
              style={{
                marginRight: '5px',
                marginBottom: '5px',
                padding: '5px 10px',
                border: '1px solid #ccc',
                borderRadius: '3px',
                cursor: 'pointer',
                backgroundColor: '#fff'
              }}
            >
              {constant.name} ({constant.value.toFixed(4)})
            </button>
          ))}
        </div>
      </div>
    </PanelShell>
  );
};

export default ArithmeticPanel;
